import React, { useState } from 'react';
import { APP } from '../../app/appConfig';
import { cn } from '../../ui';
import { AppleIcon, PlayIcon } from '../storeIcons';

/**
 * Each tagline calls out a public, shipped native-app feature the website
 * cannot do. One is picked at random per mount.
 *
 * Feature attribution:
 *  1. Offline-first SQLite library — web requires a connection.
 *  2. Live satsang channels (followers) — broadcast playback the web can't tune into.
 *  3. Selective language pack downloads + offline library + live channels —
 *     three concrete capabilities the browser can't do.
 */
const TAGLINES = [
  'The website needs Wi-Fi. The app works offline.',
  'The web shows the lyrics. The app tunes you into live satsang.',
  'Offline library, live satsang channels, language packs you keep — three things the browser can’t do.',
];

/**
 * "Get the app" promo. Lives in the footer / settings, surfaces both stores.
 * On iOS Safari the OS-level "apple-itunes-app" smart banner (set in
 * public/index.html) handles the deep-link prompt; this is the explicit
 * web fallback for everyone else.
 *
 * Only rendered where APP.features.appDownload is on, which is the same
 * condition under which APP.storeUrls is set.
 */
export const AppDownload: React.FC<{ className?: string }> = ({ className }) => {
  // Lazy initializer — one tagline picked per component mount.
  const [tagline] = useState(() => TAGLINES[Math.floor(Math.random() * TAGLINES.length)]);
  const { siteName } = APP.brand;
  const stores = APP.storeUrls!;

  return (
    <section
      aria-labelledby="app-download-heading"
      className={cn(
        'rounded-2xl border border-light bg-bg-2/60 px-5 py-5 sm:px-6 sm:py-6',
        className
      )}
    >
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-5">
        <div className="flex-1 min-w-0">
          <h3 id="app-download-heading" className="font-display text-h3 font-semibold mb-1">
            Get the app
          </h3>
          <p className="text-small text-text-secondary">{tagline}</p>
        </div>
        <div className="flex flex-wrap gap-3 shrink-0">
          <a
            href={stores.ios}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`Download ${siteName} on the App Store`}
            className="inline-flex items-center gap-2 rounded-xl border border-light bg-bg-3 hover:bg-bg-4 hover:border-strong px-4 h-12 transition-colors"
          >
            <AppleIcon />
            <span className="flex flex-col leading-tight">
              <span className="text-[10px] uppercase tracking-wide text-text-tertiary">Download on the</span>
              <span className="text-small font-semibold">App Store</span>
            </span>
          </a>
          <a
            href={stores.android}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`Get ${siteName} on Google Play`}
            className="inline-flex items-center gap-2 rounded-xl border border-light bg-bg-3 hover:bg-bg-4 hover:border-strong px-4 h-12 transition-colors"
          >
            <PlayIcon />
            <span className="flex flex-col leading-tight">
              <span className="text-[10px] uppercase tracking-wide text-text-tertiary">Get it on</span>
              <span className="text-small font-semibold">Google Play</span>
            </span>
          </a>
        </div>
      </div>
    </section>
  );
};
